import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import { parse, isValid } from "date-fns";

import formOptions from "../../config/formOptions.json";
import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import MarkdownEditor from "../../components/MarkdownEditor";
import LabelSelector from "../../components/LabelSelector";
import DateInput from "../../components/DateInput";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";

import { graphqlEndpoint } from "../../config";
import { parseUKDate } from "../../utils/dateUtiles";
import { READ_TASK_BY_ID } from "../../graph/tasks/queries";
import { READ_TENANT_ROLES_BY_TENANT } from "../../graph/tenantRoles/queries";
import { READ_ALL_USERS } from "../../graph/users/queries";
import { DELETE_TASK, UPDATE_TASK } from "../../graph/tasks/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const allowedLabels = (formOptions?.labelOptions || []).map((l) => l.value) || [];

const validationSchema = yup.object().shape({
  title: yup.string().required("required"),
  description: yup.string(),
  status: yup.string().required("required"),
  labels: yup
    .array()
    .of(yup.string().oneOf(allowedLabels, "Invalid label"))
    .nullable(),
  accountableRoleId: yup.string().nullable(),
  assignedTenantUserId: yup.string().nullable(),
  dueDate: yup.string(),
  deferDate: yup.string(),
  plannedDurationMin: yup
    .number()
    .typeError("Planned minutes must be a number")
    .integer("Use whole minutes")
    .min(0, "Must be ≥ 0")
    .nullable(),
  actualDurationMin: yup
    .number()
    .typeError("Actual minutes must be a number")
    .integer("Use whole minutes")
    .min(0, "Must be ≥ 0")
    .nullable(),
});

const EditTaskForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const [initialValues, setInitialValues] = useState(null);
  const [roles, setRoles] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);

  const redirectPath = location.state?.redirectPath || "/manageTasks";

  const numOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? null
      : parseInt(v, 10);

  const floatOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? null
      : parseFloat(v);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      await client.request(DELETE_TASK, { id: String(id) });
      setSnackbar({
        open: true,
        severity: "success",
        message: "Task deleted successfully",
      });
      navigate(redirectPath);
    } catch (err) {
      console.error("Delete failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete task",
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId) return;

      try {
        const [{ readTaskById }, { readTenantRolesByTenant }, { readAllUsers }] =
          await Promise.all([
            client.request(READ_TASK_BY_ID, { id }),
            client.request(READ_TENANT_ROLES_BY_TENANT, { tenantId }),
            client.request(READ_ALL_USERS),
          ]);

        if (readTaskById.tenantId !== tenantId) {
          setSnackbar({
            open: true,
            severity: "error",
            message: "This task does not belong to the selected tenant",
          });
          navigate(redirectPath);
          return;
        }

        setRoles(readTenantRolesByTenant || []);
        setTenantUsers(
          (readAllUsers || []).filter(
            (user) => user.tenantID === tenantId && !user.archivedAt
          )
        );

        setInitialValues({
          title: readTaskById.title || "",
          description: readTaskById.description || "",
          status: readTaskById.status || "INBOX",
          sequence: readTaskById.sequence || "",
          labels: readTaskById.labels || [],
          accountableRoleId: readTaskById.accountableRoleId || "",
          assignedTenantUserId: readTaskById.assignedTenantUserId || "",
          dueDate:
            readTaskById.dueDate &&
            isValid(parse(readTaskById.dueDate, "dd-MM-yyyy", new Date()))
              ? readTaskById.dueDate
              : "",
          deferDate:
            readTaskById.deferDate &&
            isValid(parse(readTaskById.deferDate, "dd-MM-yyyy", new Date()))
              ? readTaskById.deferDate
              : "",
          cost: readTaskById.cost ?? "",
          plannedDurationMin: readTaskById.plannedDurationMin ?? "",
          actualDurationMin: readTaskById.actualDurationMin ?? "",
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load task",
        });
      }
    };

    fetchData();
  }, [id, tenantId, navigate, redirectPath, setSnackbar]);

  const handleFormSubmit = async (values) => {
    const cleanedValues = { ...values };

    for (const key of Object.keys(cleanedValues)) {
      if (cleanedValues[key] === "") cleanedValues[key] = null;
    }

    cleanedValues.id = id;
    cleanedValues.tenantId = tenantId;
    cleanedValues.projectId = null;
    cleanedValues.labels = cleanedValues.labels || [];
    cleanedValues.dueDate = cleanedValues.dueDate
      ? parseUKDate(cleanedValues.dueDate)
      : null;
    cleanedValues.deferDate = cleanedValues.deferDate
      ? parseUKDate(cleanedValues.deferDate)
      : null;
    cleanedValues.plannedDurationMin = numOrNull(cleanedValues.plannedDurationMin);
    cleanedValues.actualDurationMin = numOrNull(cleanedValues.actualDurationMin);
    cleanedValues.cost = floatOrNull(cleanedValues.cost);

    try {
      await client.request(UPDATE_TASK, {
        input: cleanedValues,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Task updated successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Update failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update task",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header title="EDIT TASK" subtitle="Select a tenant before editing a task" />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="EDIT TASK"
          subtitle={`Edit task: ${initialValues.title}${
            tenantName ? ` for ${tenantName}` : ""
          }`}
        />
        <Tooltip title="Delete">
          <ActionButton
            tone="danger"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete Task
          </ActionButton>
        </Tooltip>
      </Box>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="20px" gridTemplateColumns="repeat(4, minmax(0, 1fr))">
              <TextField
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                tenantId={tenantId}
                taskId={id}
                enableImageUpload
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              <LabelSelector
                selectedLabels={values.labels}
                setFieldValue={setFieldValue}
                error={errors.labels}
                touched={touched.labels}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Status"
                >
                  {(formOptions.taskStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Sequence"
                name="sequence"
                value={values.sequence}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Accountable Role</InputLabel>
                <Select
                  name="accountableRoleId"
                  value={values.accountableRoleId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Accountable Role"
                >
                  <MenuItem value="">None</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Assigned User</InputLabel>
                <Select
                  name="assignedTenantUserId"
                  value={values.assignedTenantUserId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Assigned User"
                >
                  <MenuItem value="">None</MenuItem>
                  {tenantUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                        user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateInput
                label="Due Date"
                name="dueDate"
                value={values.dueDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.dueDate}
                touched={touched.dueDate}
              />

              <DateInput
                label="Defer Until"
                name="deferDate"
                value={values.deferDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.deferDate}
                touched={touched.deferDate}
              />

              <TextField
                label="Planned minutes"
                name="plannedDurationMin"
                type="number"
                value={values.plannedDurationMin}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 1 }}
                error={!!touched.plannedDurationMin && !!errors.plannedDurationMin}
                helperText={touched.plannedDurationMin && errors.plannedDurationMin}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Actual minutes"
                name="actualDurationMin"
                type="number"
                value={values.actualDurationMin}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 1 }}
                error={!!touched.actualDurationMin && !!errors.actualDurationMin}
                helperText={touched.actualDurationMin && errors.actualDurationMin}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Cost"
                name="cost"
                type="number"
                value={values.cost}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 0.01 }}
                error={!!touched.cost && !!errors.cost}
                helperText={touched.cost && errors.cost}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditTaskForm;